import { record } from 'rrweb';
import Sender from './sender';
import RecordingManager from './recordingManager';
import Timer from './timer';
import SessionManager from '../session/sessionManager';

// set idle time to 60 seconds
const MAX_IDLE_TIME = 60 * 1000;
const SCREEN_VIEW_EVENT_COOLDOWN = 5 * 1000;

export default class Agent {
  recordingManager: RecordingManager;
  sender: Sender;
  MAX_IDLE_TIME: any;
  timer: Timer | undefined;
  sendEvents: ((events: any[]) => Promise<void>) | undefined;
  sendScreenViewEvent: ((props: any) => void) | undefined;
  recordingBlocklist: string[] = [];
  _allowLocalhostRecording?: boolean;
  private sessionCreationPromise: Promise<void> | null = null;
  private static _sessionManager: SessionManager = SessionManager.getInstance();

  get sessionManager(): SessionManager {
    return Agent._sessionManager;
  }

  constructor() {
    this.recordingManager = new RecordingManager(this);
    this.sender = new Sender(this);
  }

  async start({
    sendEvents,
    sendScreenViewEvent,
    recordingBlocklist,
    _allowLocalhostRecording,
  }: {
    sendEvents: (events: any[]) => Promise<void>;
    sendScreenViewEvent: (props: any) => void;
    recordingBlocklist: string[];
    _allowLocalhostRecording?: boolean;
  }) {
    this.sendEvents = sendEvents;
    this.sendScreenViewEvent = sendScreenViewEvent;
    this.recordingBlocklist = recordingBlocklist;
    this._allowLocalhostRecording = _allowLocalhostRecording;
    this.MAX_IDLE_TIME = MAX_IDLE_TIME || 60 * 1000;
    this.timer = new Timer(this, this.MAX_IDLE_TIME);

    this.sender.start();
    this.recordingManager.start();
  }

  handleTimeout() {
    if (this.recordingManager.recorder.stop) {
      this.recordingManager.recorder.stop();
    }
    this.sender.send();
    this.recordingManager.startRecorderWithStasher();
    Agent._sessionManager.updateIdleStatus(true);
  }

  handleActivityDuringIdle() {
    if (Agent._sessionManager.getCurrentSessionId() !== null) {
      return;
    }

    // if a screen view event was sent less than 5 seconds ago, do not send another one
    if (
      Agent._sessionManager.getLastScreenViewEventTime() >
      Date.now() - SCREEN_VIEW_EVENT_COOLDOWN
    ) {
      return;
    }

    // if session creation is already in progress, do not attempt to create another one
    if (this.sessionCreationPromise) {
      return;
    }

    this.sessionCreationPromise = this.createSession().finally(() => {
      this.sessionCreationPromise = null;
    });
  }

  takeFullSnapshot() {
    return this.recordingManager.recorder.takeFullSnapshot();
  }

  static handleCustomEvent(customEventType: string) {
    record.addCustomEvent(customEventType, null);
  }

  private async createSession(): Promise<void> {
    if (!this.sendScreenViewEvent) {
      return;
    }

    try {
      this.sendScreenViewEvent({
        __path: window.location.href,
        __title: document.title,
        __is_session_refresh: true,
      });
    } catch (error) {
      console.error('Failed to send screen_view event:', error);
    }
  }
}
